import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../SleepHub/sleepHubApi';
import { formatMoney, formatMoneyExact } from './marketplaceHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  paletteAt
} from '../../component/common/designSystem';
import {
  ORDER_STATUS_COLORS,
  ORDER_TYPE_COLORS,
  PROVIDER_COLORS,
  VALUE_RAMP,
  ORDERS_PER_USER_RAMP
} from './marketplaceTheme';

/**
 * Marketplace → E-Gifts Dashboard.
 *
 * What this company's people actually redeemed, read as four bands:
 *  1. Headline — volume, spend and how much of it went through cleanly.
 *  2. Distributions — order status, who it was for, fulfilment route, face
 *     value and how many orders each person places.
 *  3. Trends — orders and spend per day over the last 30 days.
 *  4. Reach and brands — how far redemption spread, and what people chose.
 *
 * SCOPING: voucher orders carry no usable company_id (nothing writes it), so
 * the backend scopes by employee user ids. Everything here is therefore
 * already narrowed to the population — see services/marketplaceHubService.js.
 */
const EGiftsDashboard = ({ fetcher, populationLabel = 'employees' }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const ordersPerDay = stats?.ordersPerDay || [];
  const valuePerDay = stats?.valuePerDay || [];
  const statusSplit = stats?.statusSplit || [];
  const typeSplit = stats?.typeSplit || [];
  const providerSplit = stats?.providerSplit || [];
  const valueDistribution = stats?.valueDistribution || [];
  const ordersPerUser = stats?.ordersPerUserDistribution || [];
  const weekday = stats?.weekdayOrders || [];
  const topBrands = stats?.topBrands || [];

  const trendLabels = lastNDaysLabels(ordersPerDay.length);

  // What share of the population has ever redeemed anything.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.redeemers / stats.totalUsers) * 100) : null;

  // Orders that never completed — the support-shaped half of the status donut.
  const unresolvedOrders = Math.max(
    (stats?.totalOrders || 0) - (stats?.successfulOrders || 0),
    0
  );

  /**
   * Orders and spend on one chart, two axes: a spike in order count that the
   * spend line doesn't follow means people are taking small vouchers, which is
   * the opposite conclusion from the bars alone.
   */
  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { curve: 'smooth', width: [2, 2], dashArray: [0, 4] },
    fill: { type: ['gradient', 'solid'], gradient: AREA_FILL.gradient },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: trendLabels, tickAmount: 6 },
    yaxis: [
      { seriesName: 'Orders', labels: { formatter: (val) => formatBigNumber(val) } },
      {
        seriesName: 'Spend',
        opposite: true,
        labels: { formatter: (val) => formatMoney(val) }
      }
    ],
    tooltip: {
      shared: true,
      y: [
        { formatter: (val) => `${val.toLocaleString()} orders` },
        { formatter: (val) => formatMoneyExact(val) }
      ]
    }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekday.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} orders` } }
  };

  // Colour-coded per brand so the bar chart and the table below read as the
  // same ranking.
  const brandOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: topBrands.map((brand, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '65%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: topBrands.map((brand) => brand.name) },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} orders` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>E-Gifts Dashboard | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Total Orders"
          value={stats ? formatBigNumber(stats.totalOrders) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.rating}
          title="Total Spend"
          value={stats ? formatMoney(stats.totalValue) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.users}
          title="People Redeeming"
          value={stats ? formatBigNumber(stats.redeemers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Avg Order Value"
          value={stats ? formatMoney(stats.avgOrderValue) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Fulfilled"
          value={stats ? `${stats.successRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.plays}
          title="Sent as Gifts"
          value={stats ? formatBigNumber(stats.giftedOrders) : '-'}
        />
      </div>

      {/* ---------- Distribution summary ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4 mt-6">
        <MiniDonutCard
          title="Order Status"
          subtitle="How orders resolved"
          data={statusSplit}
          colors={ORDER_STATUS_COLORS}
          unit="orders"
        />
        <MiniDonutCard
          title="Who It Was For"
          subtitle="Self vs gifted to someone"
          data={typeSplit}
          colors={ORDER_TYPE_COLORS}
          unit="orders"
          legendLimit={2}
        />
        <MiniDonutCard
          title="Fulfilment Route"
          subtitle="Which provider issued it"
          data={providerSplit}
          colors={PROVIDER_COLORS}
          unit="orders"
        />
        <MiniDonutCard
          title="Voucher Value"
          subtitle="Face value per order"
          data={valueDistribution}
          colors={VALUE_RAMP}
          unit="orders"
        />
        <MiniDonutCard
          title="Orders per Person"
          subtitle="How often people come back"
          data={ordersPerUser}
          colors={ORDERS_PER_USER_RAMP}
          unit={populationLabel}
        />
      </div>

      {/* ---------- Trends ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Orders & Spend" subtitle="Per day — last 30 days">
          {ordersPerDay.some((value) => value > 0) ? (
            <Chart
              type="line"
              height={340}
              width="100%"
              series={[
                { name: 'Orders', type: 'area', data: ordersPerDay },
                { name: 'Spend', type: 'line', data: valuePerDay }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No e-gift orders in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Orders by Weekday" subtitle="When people redeem">
          {weekday.some((day) => day.orders > 0) ? (
            <Chart
              type="bar"
              height={340}
              width="100%"
              series={[{ name: 'Orders', data: weekday.map((day) => day.orders) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No e-gift orders recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Reach ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Redemption Reach"
          subtitle={`Share of ${populationLabel} who redeemed`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.redeemers)} of ${formatBigNumber(
                stats.totalUsers
              )} ${populationLabel}`
              : null
          }
        />

        <Panel
          className="lg:col-span-2"
          title="Most Redeemed Brands"
          subtitle={`Top ${topBrands.length || 10} by order count`}
        >
          {topBrands.length > 0 ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Orders', data: topBrands.map((brand) => brand.orders) }]}
              options={brandOptions}
            />
          ) : (
            <PanelEmpty>No e-gift orders recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Fulfilment ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/*
          Fulfilment health, not order count: the number worth acting on is how
          many orders never reached the person who placed them.
        */}
        <Panel title="Fulfilment Health" subtitle="Where orders are getting stuck">
          <ProgressList
            items={[
              { label: 'Fulfilled', count: stats?.successfulOrders || 0 },
              { label: 'Still in flight or failed', count: unresolvedOrders }
            ]}
            unit="orders"
          />
        </Panel>

        <Panel
          className="lg:col-span-2"
          title="Brand Breakdown"
          subtitle="Orders, spend and distinct buyers per brand"
        >
          {topBrands.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">#</th>
                  <th className="py-2 pr-4 font-medium">Brand</th>
                  <th className="py-2 pr-4 font-medium text-right">Orders</th>
                  <th className="py-2 pr-4 font-medium text-right">Spend</th>
                  <th className="py-2 font-medium text-right">Buyers</th>
                </>
              }
            >
              {topBrands.map((brand, index) => (
                <tr key={brand.id || index} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">
                    {/* Rank swatch ties each row to its bar in the chart. */}
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: paletteAt(index) }}
                      />
                      {index + 1}
                    </span>
                  </td>
                  <td className="py-2 pr-4">{brand.name}</td>
                  <td className="py-2 pr-4 text-right">{brand.orders.toLocaleString()}</td>
                  <td className="py-2 pr-4 text-right">{formatMoneyExact(brand.value)}</td>
                  <td className="py-2 text-right">{brand.buyers}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No e-gift orders recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

EGiftsDashboard.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string
};

export default EGiftsDashboard;
